import { useState, useCallback, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  FileSearch,
  Download,
  Copy,
  Loader2,
  CheckCheck,
  RefreshCw,
  Stethoscope,
  FileText,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  FileCode,
  Eye,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { useAdminGuard } from "@/hooks/useAdminGuard";
import { useHeartbeatDiagnostics } from "@/hooks/useAdminBrain";

// ── Helpers ──

function formatMs(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function statusBadge(status: string) {
  if (status === "operational") return <Badge className="bg-emerald-500/20 text-emerald-700 border-emerald-500/30 text-[11px]">Operational</Badge>;
  if (status === "partial") return <Badge className="bg-yellow-500/20 text-yellow-700 border-yellow-500/30 text-[11px]">Partial</Badge>;
  if (status === "stub" || status === "missing") return <Badge className="bg-red-500/20 text-red-700 border-red-500/30 text-[11px]">Not Running</Badge>;
  return <Badge className="bg-gray-500/20 text-[#746F66] text-[11px]">{status}</Badge>;
}

function severityIcon(severity: string) {
  if (severity === "critical") return <XCircle className="h-4 w-4 text-red-700 flex-shrink-0" />;
  if (severity === "high") return <AlertTriangle className="h-4 w-4 text-orange-700 flex-shrink-0" />;
  if (severity === "medium") return <AlertTriangle className="h-4 w-4 text-yellow-700 flex-shrink-0" />;
  return <CheckCircle className="h-4 w-4 text-[#746F66] flex-shrink-0" />;
}

function severityBadge(severity: string) {
  const map: Record<string, string> = {
    critical: "bg-red-500/20 text-red-700 border-red-500/30",
    high: "bg-orange-500/20 text-orange-700 border-orange-500/30",
    medium: "bg-yellow-500/20 text-yellow-700 border-yellow-500/30",
    low: "bg-gray-500/20 text-[#746F66] border-gray-500/30",
  };
  return <Badge className={cn("text-[11px] capitalize", map[severity] || map.low)}>{severity}</Badge>;
}

function sectionHealthBadge(section: Record<string, unknown> | null | undefined): React.ReactNode {
  if (!section) return <Badge className="bg-gray-500/20 text-[#746F66] text-[11px]">—</Badge>;
  if (section.error) return <Badge className="bg-red-500/20 text-red-700 border-red-500/30 text-[11px]">Error</Badge>;
  return <Badge className="bg-emerald-500/20 text-emerald-700 border-emerald-500/30 text-[11px]">OK</Badge>;
}

function pipelinesSectionBadge(pipelineAudit: Record<string, unknown> | null | undefined): React.ReactNode {
  if (!pipelineAudit) return <Badge className="bg-gray-500/20 text-[#746F66] text-[11px]">—</Badge>;
  if (pipelineAudit.error) return <Badge className="bg-red-500/20 text-red-700 border-red-500/30 text-[11px]">Error</Badge>;
  const pipelines = Object.entries(pipelineAudit).filter(([k]) => k !== "timeMs");
  const stubs = pipelines.filter(([, v]) => (v as Record<string, unknown>).status === "stub").length;
  if (stubs === pipelines.length) return <Badge className="bg-red-500/20 text-red-700 border-red-500/30 text-[11px]">None Running</Badge>;
  if (stubs > 0) return <Badge className="bg-yellow-500/20 text-yellow-700 border-yellow-500/30 text-[11px]">{pipelines.length - stubs}/{pipelines.length} Active</Badge>;
  return <Badge className="bg-emerald-500/20 text-emerald-700 border-emerald-500/30 text-[11px]">All Active</Badge>;
}

function embeddingBadge(ed: Record<string, unknown> | null | undefined): React.ReactNode {
  if (!ed) return <Badge className="bg-gray-500/20 text-[#746F66] text-[11px]">—</Badge>;
  if (ed.error) return <Badge className="bg-red-500/20 text-red-700 border-red-500/30 text-[11px]">Error</Badge>;
  const testResults = ed.testResults as Record<string, Record<string, unknown>> | undefined;
  const isZero = testResults?.test1?.isAllZeros;
  const diffOk = testResults?.test2?.isDifferentFromTest1;
  if (isZero || !diffOk) return <Badge className="bg-red-500/20 text-red-700 border-red-500/30 text-[11px]">Broken</Badge>;
  return <Badge className="bg-emerald-500/20 text-emerald-700 border-emerald-500/30 text-[11px]">Healthy</Badge>;
}

function retrievalBadge(rd: Record<string, unknown> | null | undefined): React.ReactNode {
  if (!rd) return <Badge className="bg-gray-500/20 text-[#746F66] text-[11px]">—</Badge>;
  if (rd.error) return <Badge className="bg-red-500/20 text-red-700 border-red-500/30 text-[11px]">Error</Badge>;
  if (!rd.isIntegratedWithAskmoji) return <Badge className="bg-red-500/20 text-red-700 border-red-500/30 text-[11px]">Not Integrated</Badge>;
  return <Badge className="bg-emerald-500/20 text-emerald-700 border-emerald-500/30 text-[11px]">Integrated</Badge>;
}

function schedulerBadge(sd: Record<string, unknown> | null | undefined): React.ReactNode {
  if (!sd) return <Badge className="bg-gray-500/20 text-[#746F66] text-[11px]">—</Badge>;
  if (sd.error) return <Badge className="bg-red-500/20 text-red-700 border-red-500/30 text-[11px]">Error</Badge>;
  const hasDaily = !!(sd.lastDailyRun);
  const hasWeekly = !!(sd.lastWeeklyRun);
  if (!hasDaily && !hasWeekly) return <Badge className="bg-yellow-500/20 text-yellow-700 border-yellow-500/30 text-[11px]">Never Ran</Badge>;
  return <Badge className="bg-emerald-500/20 text-emerald-700 border-emerald-500/30 text-[11px]">Active</Badge>;
}

function dataSourceBadge(ds: Record<string, unknown> | null | undefined): React.ReactNode {
  if (!ds) return <Badge className="bg-gray-500/20 text-[#746F66] text-[11px]">—</Badge>;
  if (ds.error) return <Badge className="bg-red-500/20 text-red-700 border-red-500/30 text-[11px]">Error</Badge>;
  const accounts = ds.adAccounts as Record<string, number> | undefined;
  const gap = accounts?.gap ?? 0;
  if (gap > 0) return <Badge className="bg-yellow-500/20 text-yellow-700 border-yellow-500/30 text-[11px]">{gap} Gap(s)</Badge>;
  return <Badge className="bg-emerald-500/20 text-emerald-700 border-emerald-500/30 text-[11px]">All Linked</Badge>;
}

function graphBadge(gi: Record<string, unknown> | null | undefined): React.ReactNode {
  if (!gi) return <Badge className="bg-gray-500/20 text-[#746F66] text-[11px]">—</Badge>;
  if (gi.error) return <Badge className="bg-red-500/20 text-red-700 border-red-500/30 text-[11px]">Error</Badge>;
  const orphaned = (gi.orphanedEdges as number) ?? 0;
  const dupes = (gi.duplicateEdges as number) ?? 0;
  if (orphaned > 0 || dupes > 0) return <Badge className="bg-yellow-500/20 text-yellow-700 border-yellow-500/30 text-[11px]">Issues Found</Badge>;
  return <Badge className="bg-emerald-500/20 text-emerald-700 border-emerald-500/30 text-[11px]">Healthy</Badge>;
}

function codeBadge(ci: Record<string, unknown> | null | undefined): React.ReactNode {
  if (!ci) return <Badge className="bg-gray-500/20 text-[#746F66] text-[11px]">—</Badge>;
  if (ci.error) return <Badge className="bg-red-500/20 text-red-700 border-red-500/30 text-[11px]">Error</Badge>;
  const files = ci.files as Record<string, { exists: boolean }> | undefined;
  const missing = files ? Object.values(files).filter(f => !f.exists).length : 0;
  if (missing > 0) return <Badge className="bg-red-500/20 text-red-700 border-red-500/30 text-[11px]">{missing} Missing</Badge>;
  return <Badge className="bg-emerald-500/20 text-emerald-700 border-emerald-500/30 text-[11px]">All Present</Badge>;
}

function gapBadge(ga: Record<string, unknown> | null | undefined): React.ReactNode {
  if (!ga) return <Badge className="bg-gray-500/20 text-[#746F66] text-[11px]">—</Badge>;
  if (ga.error) return <Badge className="bg-red-500/20 text-red-700 border-red-500/30 text-[11px]">Error</Badge>;
  const issues = (ga.issues as Array<{ severity: string }>) ?? [];
  const critical = issues.filter(i => i.severity === "critical").length;
  const high = issues.filter(i => i.severity === "high").length;
  if (critical > 0) return <Badge className="bg-red-500/20 text-red-700 border-red-500/30 text-[11px]">{critical} Critical</Badge>;
  if (high > 0) return <Badge className="bg-orange-500/20 text-orange-700 border-orange-500/30 text-[11px]">{high} High</Badge>;
  if (issues.length > 0) return <Badge className="bg-yellow-500/20 text-yellow-700 border-yellow-500/30 text-[11px]">{issues.length} Issues</Badge>;
  return <Badge className="bg-emerald-500/20 text-emerald-700 border-emerald-500/30 text-[11px]">No Issues</Badge>;
}

function schemaBadge(rts: Record<string, unknown> | null | undefined): React.ReactNode {
  if (!rts) return <Badge className="bg-gray-500/20 text-[#746F66] text-[11px]">—</Badge>;
  if (rts.error) return <Badge className="bg-red-500/20 text-red-700 border-red-500/30 text-[11px]">Error</Badge>;
  const schemas = rts.schemas as Record<string, unknown[]> | undefined;
  const tableCount = schemas ? Object.keys(schemas).length : 0;
  return <Badge className="bg-emerald-500/20 text-emerald-700 border-emerald-500/30 text-[11px]">{tableCount} Tables</Badge>;
}

// ── Collapsible Section Card ──

interface SectionCardProps {
  title: string;
  icon: React.ReactNode;
  statusEl?: React.ReactNode;
  timeMs?: number;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

function SectionCard({ title, icon, statusEl, timeMs, children, defaultOpen = false }: SectionCardProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="rounded-xl border border-[#E5E2DC] bg-[#F0EDE7] overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-[#F0EDE7] transition-colors"
        data-testid={`section-${title.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <span className="text-[#746F66]">{icon}</span>
        <span className="flex-1 text-sm font-semibold text-[#1A1A1A]">{title}</span>
        {timeMs !== undefined && (
          <span className="text-[11px] text-[#746F66] flex items-center gap-1">
            <Clock className="h-3 w-3" />{formatMs(timeMs)}
          </span>
        )}
        {statusEl}
        {open ? <ChevronDown className="h-4 w-4 text-[#746F66] flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-[#746F66] flex-shrink-0" />}
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-[#E5E2DC]">
          {children}
        </div>
      )}
    </div>
  );
}

// ── Data Table Helper ──

function DataRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 py-1.5 border-b border-[#E5E2DC] last:border-0">
      <span className="text-[12px] text-[#746F66] flex-shrink-0 w-48">{label}</span>
      <span className="text-[12px] text-[#3D3A36] flex-1 break-all">{value ?? "—"}</span>
    </div>
  );
}

// ── MD Report Generator ──

function generateMdReport(data: Record<string, unknown>): string {
  const ts = new Date().toISOString().replace("T", " ").slice(0, 19);
  const lines: string[] = [];

  lines.push("# Heartbeat AI Intelligence Memory — Diagnostic Report");
  lines.push(`Generated: ${ts}`);
  lines.push("Platform: AdMoji AdManager");
  lines.push("");

  const gapIssues = ((data.gapAnalysis as Record<string, unknown>)?.issues as Array<{ severity: string }>) ?? [];
  const criticalCount = gapIssues.filter(i => i.severity === "critical").length;
  const highCount = gapIssues.filter(i => i.severity === "high").length;
  const pipelineAudit = data.pipelineAudit as Record<string, unknown> | undefined;
  const activePipelines = pipelineAudit
    ? Object.entries(pipelineAudit).filter(([k, v]) => k !== "timeMs" && (v as Record<string, unknown>).status === "operational").length
    : 0;
  const totalPipelines = pipelineAudit
    ? Object.entries(pipelineAudit).filter(([k]) => k !== "timeMs").length
    : 6;

  const overallStatus = criticalCount > 0 ? "Critical" : highCount > 0 ? "Degraded" : "Healthy";
  const th = data.tableHealth as Record<string, Record<string, unknown>> | undefined;
  const ed = data.embeddingDiagnostic as Record<string, unknown> | undefined;
  const rd = data.retrievalDiagnostic as Record<string, unknown> | undefined;

  lines.push("## Summary");
  lines.push(`- Overall Status: ${overallStatus}`);
  lines.push(`- Total Chunks: ${th?.chunks?.totalActiveCount ?? 0}`);
  lines.push(`- Total Connections: ${th?.connections?.totalCount ?? 0}`);
  lines.push(`- Embedding Health: ${(ed?.testResults as Record<string, Record<string, unknown>>)?.test1?.hasValidFloats ? "Pass" : "Fail"}`);
  lines.push(`- Retrieval Health: ${rd?.serviceExists ? "Pass" : "Fail"}`);
  lines.push(`- Active Pipelines: ${activePipelines}/${totalPipelines}`);
  lines.push(`- Critical Issues: ${criticalCount}`);
  lines.push(`- High Issues: ${highCount}`);
  lines.push(`- Diagnostic Time: ${data.totalTimeMs}ms`);
  lines.push("");

  lines.push("## 1. Database Table Health");
  if (th?.error) {
    lines.push(`> Error: ${th.error}`);
  } else if (th) {
    const chunks = th.chunks as Record<string, unknown>;
    const connections = th.connections as Record<string, unknown>;
    const digests = th.digests as Record<string, unknown>;
    const settings = th.settings as Record<string, unknown>;
    lines.push("### heartbeat_chunks");
    lines.push(`- Active Chunks: ${chunks?.totalActiveCount}`);
    lines.push(`- Archived Chunks: ${chunks?.totalArchivedCount}`);
    lines.push(`- Chunks With Embeddings: ${chunks?.chunksWithEmbeddings}`);
    lines.push(`- Chunks Without Embeddings: ${chunks?.chunksWithoutEmbeddings}`);
    lines.push(`- Zero Vector Embeddings: ${chunks?.chunksWithZeroEmbeddings}`);
    lines.push(`- Average Importance: ${(chunks?.averageImportance as number)?.toFixed(2)}`);
    lines.push(`- Expired Chunks: ${chunks?.expiredChunksCount}`);
    lines.push(`- Oldest Chunk: ${chunks?.oldestChunkDate ?? "N/A"}`);
    lines.push(`- Newest Chunk: ${chunks?.newestChunkDate ?? "N/A"}`);
    lines.push("");
    lines.push("**Chunks by Category:**");
    for (const [cat, count] of Object.entries((chunks?.countByCategory as Record<string, unknown>) ?? {})) {
      lines.push(`- ${cat}: ${count}`);
    }
    lines.push("");
    lines.push("### heartbeat_connections");
    lines.push(`- Total Connections: ${connections?.totalCount}`);
    lines.push(`- Strong (>=0.7): ${connections?.strongConnections}`);
    lines.push(`- Moderate (0.4-0.7): ${connections?.moderateConnections}`);
    lines.push(`- Weak (<0.4): ${connections?.weakConnections}`);
    lines.push(`- Orphaned: ${connections?.orphanedConnections}`);
    lines.push("");
    lines.push("### heartbeat_digests");
    lines.push(`- Total Digests: ${digests?.totalCount}`);
    lines.push(`- Most Recent: ${digests?.mostRecentDigestDate ?? "N/A"}`);
    for (const [type, count] of Object.entries((digests?.countByType as Record<string, unknown>) ?? {})) {
      lines.push(`- ${type}: ${count}`);
    }
    lines.push("");
    lines.push("### heartbeat_settings");
    lines.push(`- Total Settings Rows: ${settings?.totalCount}`);
    lines.push(`- Accounts With Disabled: ${(settings?.accountsWithAllPipelinesDisabled as unknown[])?.length ?? 0}`);
  }
  lines.push("");

  lines.push("## 2. Pipeline Status Audit");
  if (pipelineAudit?.error) {
    lines.push(`> Error: ${pipelineAudit.error}`);
  } else if (pipelineAudit) {
    for (const [name, p] of Object.entries(pipelineAudit) as [string, Record<string, unknown>][]) {
      if (name === "timeMs") continue;
      lines.push(`### ${name}`);
      lines.push(`- Function: ${p.functionName}`);
      lines.push(`- Status: ${p.status}`);
      lines.push(`- Chunks Produced: ${p.chunksProduced ?? p.digestsProduced ?? "N/A"}`);
      if (p.lastChunkCreated) lines.push(`- Last Chunk: ${p.lastChunkCreated}`);
      if (p.lastDigestDate) lines.push(`- Last Digest: ${p.lastDigestDate}`);
      if (p.schedulerRegistered !== undefined) lines.push(`- Scheduler: ${p.schedulerRegistered ? p.scheduleExpression : "Not registered"}`);
      if (p.triggerDescription) lines.push(`- Trigger: ${p.triggerDescription}`);
      lines.push("");
    }
  }

  lines.push("## 3. Embedding Service");
  if (ed?.error) {
    lines.push(`> Error: ${ed.error}`);
  } else if (ed) {
    const testResults = ed.testResults as Record<string, Record<string, unknown>> | undefined;
    const chunksAnalysis = ed.chunksAnalysis as Record<string, unknown> | undefined;
    lines.push(`- Method: ${ed.generationMethod}`);
    lines.push(`- Test 1 Dimension: ${testResults?.test1?.outputDimension}`);
    lines.push(`- Test 1 Valid Floats: ${testResults?.test1?.hasValidFloats}`);
    lines.push(`- Test 1 All Zeros: ${testResults?.test1?.isAllZeros}`);
    lines.push(`- Tests Produce Different Vectors: ${testResults?.test2?.isDifferentFromTest1}`);
    lines.push(`- Deterministic: ${testResults?.determinismTest?.isIdenticalToTest1}`);
    lines.push(`- Chunks With Embeddings: ${chunksAnalysis?.totalChunksWithEmbeddings}`);
    lines.push(`- Corrupt Embeddings: ${chunksAnalysis?.anyCorruptEmbeddings}`);
    lines.push(`- Dimension Consistent: ${chunksAnalysis?.embeddingDimensionConsistency}`);
  }
  lines.push("");

  lines.push("## 4. Retrieval System");
  if (rd?.error) {
    lines.push(`> Error: ${rd.error}`);
  } else if (rd) {
    const config = rd.retrievalConfig as Record<string, unknown> | undefined;
    const testQueries = rd.testQueries as Array<Record<string, unknown>> | undefined;
    lines.push(`- AskMoji Integration: ${rd.isIntegratedWithAskmoji ? "Yes" : "No"}`);
    lines.push(`- Integration: ${rd.integrationDescription}`);
    lines.push(`- Max Chunks: ${config?.maxResults}`);
    lines.push(`- Max Tokens: ${config?.maxTokens}`);
    lines.push(`- User Pins Always Included: ${config?.userPinsAlwaysIncluded}`);
    if (testQueries?.length) {
      lines.push("");
      lines.push("**Test Query Results:**");
      for (const q of testQueries) {
        lines.push(`- Query: "${q.query}" -> ${q.resultsReturned ?? 0} results (${q.timeMs}ms)`);
        if (q.topResultPreview) lines.push(`  - Top result: ${String(q.topResultPreview).slice(0, 100)}...`);
      }
    }
  }
  lines.push("");

  lines.push("## 5. Scheduler");
  const sd = data.schedulerDiagnostic as Record<string, unknown> | undefined;
  if (sd?.error) {
    lines.push(`> Error: ${sd.error}`);
  } else if (sd) {
    lines.push(`- Service Exists: ${sd.serviceExists}`);
    lines.push(`- Started on Boot: ${sd.isStartedOnBoot}`);
    lines.push(`- Last Daily Run: ${sd.lastDailyRun ?? "Never"}`);
    lines.push(`- Last Weekly Run: ${sd.lastWeeklyRun ?? "Never"}`);
    lines.push("");
    lines.push("**Registered Jobs:**");
    for (const job of (sd.registeredJobs as Array<Record<string, string>>) ?? []) {
      lines.push(`- ${job.name}: ${job.schedule} — ${job.description}`);
    }
  }
  lines.push("");

  lines.push("## 6. Data Source Connections");
  const ds = data.dataSourceConnections as Record<string, Record<string, unknown>> | undefined;
  if (ds?.error) {
    lines.push(`> Error: ${ds.error}`);
  } else if (ds) {
    lines.push(`**Brands:** ${ds.brands?.totalBrands} total, ${ds.brands?.brandsWithAnalysis} with analysis, gap: ${ds.brands?.gap}`);
    lines.push(`**Ad Accounts:** ${ds.adAccounts?.totalAccounts} total, ${ds.adAccounts?.accountsWithHeartbeatChunks} with Heartbeat chunks, gap: ${ds.adAccounts?.gap}`);
    lines.push(`**Creatives:** ${ds.creatives?.totalPlatformAds} platform ads, ${ds.creatives?.creativesWithHeartbeatTracking} tracked in Heartbeat`);
    lines.push(`**Competitors:** ${ds.competitors?.totalCompetitorBoards} boards, ${ds.competitors?.completedBoards} completed`);
    lines.push(`**Conversations:** ${ds.conversations?.totalConversations} sessions, ${ds.conversations?.conversationsWithEightPlusMessages} with 8+ messages, ${ds.conversations?.conversationsWithExtractedDecisions} with extracted decisions`);
    lines.push(`**Meta Ads:** ${ds.metaAds?.totalMetaAdAccounts} accounts, ${ds.metaAds?.metaInsightsRowCount} insight rows`);
    lines.push(`**TikTok:** ${ds.tiktok?.totalTiktokAccounts} accounts`);
  }
  lines.push("");

  lines.push("## 7. Graph Integrity");
  const gi = data.graphIntegrity as Record<string, unknown> | undefined;
  if (gi?.error) {
    lines.push(`> Error: ${gi.error}`);
  } else if (gi) {
    lines.push(`- Total Nodes: ${gi.totalNodes}`);
    lines.push(`- Total Edges: ${gi.totalEdges}`);
    lines.push(`- Orphaned Edges: ${gi.orphanedEdges}`);
    lines.push(`- Duplicate Edges: ${gi.duplicateEdges}`);
    lines.push(`- Self Edges: ${gi.selfEdges}`);
    lines.push(`- Isolated Nodes: ${gi.isolatedNodes}`);
    lines.push(`- Avg Connections/Node: ${(gi.averageConnectionsPerNode as number)?.toFixed(2)}`);
    const maxConn = gi.maxConnectionsOnSingleNode as Record<string, unknown> | null;
    if (maxConn) {
      lines.push(`- Most Connected: Chunk #${maxConn.chunkId} (${maxConn.connectionCount} connections, ${maxConn.category})`);
    }
    lines.push("");
    lines.push("**Strength Distribution:**");
    for (const [bucket, count] of Object.entries((gi.strengthDistribution as Record<string, unknown>) ?? {})) {
      lines.push(`- ${bucket}: ${count}`);
    }
  }
  lines.push("");

  lines.push("## 8. Code Inspection");
  const ci = data.codeInspection as Record<string, unknown> | undefined;
  if (ci?.error) {
    lines.push(`> Error: ${ci.error}`);
  } else if (ci) {
    for (const [filePath, info] of Object.entries((ci.files as Record<string, Record<string, unknown>>) ?? {})) {
      const fileStatus = info.exists ? `OK ${info.estimatedLineCount} lines` : "Missing";
      lines.push(`- \`${filePath}\`: ${fileStatus}`);
      if (info.exists && (info.exportedFunctions as string[])?.length) {
        lines.push(`  - Exports: ${(info.exportedFunctions as string[]).join(", ")}`);
      }
    }
  }
  lines.push("");

  lines.push("## 9. Gap Analysis & Issues");
  if (gapIssues.length === 0) {
    lines.push("No issues found. System appears healthy.");
  } else {
    for (const issue of gapIssues as Array<Record<string, unknown>>) {
      lines.push(`### [${String(issue.severity).toUpperCase()}] ${issue.title}`);
      lines.push(`**Category:** ${issue.category}`);
      lines.push(`**Description:** ${issue.description}`);
      lines.push(`**Recommendation:** ${issue.recommendation}`);
      lines.push("");
    }
  }

  lines.push("## 10. Raw Table Schemas");
  const rts = data.rawTableSchemas as Record<string, unknown> | undefined;
  if (rts?.error) {
    lines.push(`> Error: ${rts.error}`);
  } else if (rts) {
    for (const [tableName, cols] of Object.entries((rts.schemas as Record<string, unknown[]>) ?? {})) {
      lines.push(`### ${tableName}`);
      lines.push("| Column | Type | Nullable | Default |");
      lines.push("|--------|------|----------|---------|");
      for (const col of (cols as Array<Record<string, unknown>>)) {
        lines.push(`| ${col.column_name} | ${col.data_type} | ${col.is_nullable} | ${col.column_default ?? "—"} |`);
      }
      lines.push("");
    }
  }

  return lines.join("\n");
}

// ── Platform Report Tab ──

function formatDateFilename(isoString: string): string {
  return isoString.slice(0, 10);
}

function formatDisplayTime(isoString: string): string {
  try {
    return new Date(isoString).toLocaleString();
  } catch {
    return isoString;
  }
}

function PlatformReportTab() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState<string | null>(null);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/admin/platform-docs/generate");
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Unknown error" }));
        throw new Error(err.error ?? "Failed to generate report");
      }
      const data = await res.json();
      setContent(data.content);
      setGeneratedAt(data.generatedAt);
      toast({ title: "Report generated", description: "Platform brain dump is ready." });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      toast({ title: "Error", description: msg, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!content || !generatedAt) return;
    const filename = `admoji-platform-docs-${formatDateFilename(generatedAt)}.md`;
    const blob = new Blob([content], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCopy = async () => {
    if (!content) return;
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      toast({ title: "Copied", description: "Markdown copied to clipboard." });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: "Copy failed", description: "Could not copy to clipboard.", variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-[#F5F3EE] border border-[#E5E2DC] rounded-xl p-6 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <Button
            data-testid="button-generate-report"
            onClick={handleGenerate}
            disabled={loading}
            className="bg-[#1A1A1A] hover:bg-[#3D3A36] text-white gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Generating…
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4" />
                Generate Report
              </>
            )}
          </Button>

          {content && (
            <>
              <Button
                data-testid="button-download-md"
                variant="outline"
                onClick={handleDownload}
                className="border-[#E5E2DC] text-[#3D3A36] hover:text-[#1A1A1A] hover:bg-[#F0EDE7] gap-2"
              >
                <Download className="h-4 w-4" />
                Download MD
              </Button>

              <Button
                data-testid="button-copy-clipboard"
                variant="outline"
                onClick={handleCopy}
                className={cn(
                  "border-[#E5E2DC] text-[#3D3A36] hover:text-[#1A1A1A] hover:bg-[#F0EDE7] gap-2 transition-colors",
                  copied && "border-emerald-500/50 text-emerald-700"
                )}
              >
                {copied ? (
                  <>
                    <CheckCheck className="h-4 w-4" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    Copy to Clipboard
                  </>
                )}
              </Button>
            </>
          )}
        </div>

        {generatedAt && (
          <p data-testid="text-last-generated" className="text-xs text-[#746F66]">
            Last generated: {formatDisplayTime(generatedAt)}
          </p>
        )}
      </div>

      {loading && (
        <div
          data-testid="status-loading"
          className="bg-[#F5F3EE] border border-[#E5E2DC] rounded-xl p-12 flex flex-col items-center gap-4 text-[#746F66]"
        >
          <Loader2 className="h-10 w-10 animate-spin text-indigo-700" />
          <p className="text-sm">Assembling platform report — reading files and querying database…</p>
        </div>
      )}

      {!loading && content && (
        <div className="bg-[#F5F3EE] border border-[#E5E2DC] rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#E5E2DC] bg-[#F5F3EE]">
            <span className="text-xs font-mono text-[#746F66]">admoji-platform-docs.md</span>
            <span data-testid="text-char-count" className="text-xs text-[#746F66]">
              {content.length.toLocaleString()} chars
            </span>
          </div>
          <pre
            data-testid="pre-report-content"
            className="overflow-auto max-h-[70vh] p-5 text-xs leading-relaxed text-[#3D3A36] font-mono whitespace-pre-wrap break-words"
          >
            {content}
          </pre>
        </div>
      )}
    </div>
  );
}

// ── Heartbeat Diagnostics Tab ──

function HeartbeatDiagnosticTab() {
  const qc = useQueryClient();
  const [runEnabled, setRunEnabled] = useState(true);
  const { data, isLoading, error } = useHeartbeatDiagnostics(runEnabled);

  useEffect(() => {
    setRunEnabled(true);
  }, []);

  const handleRefresh = useCallback(() => {
    qc.removeQueries({ queryKey: ["/api/admin/heartbeat-diagnostics"] });
    setRunEnabled(false);
    setTimeout(() => setRunEnabled(true), 50);
  }, [qc]);

  const handleDownloadMd = useCallback(() => {
    if (!data) return;
    const md = generateMdReport(data);
    const now = new Date();
    const datePart = now.toISOString().slice(0, 10);
    const timePart = now.toISOString().slice(11, 19).replace(/:/g, "");
    const ts = `${datePart}-${timePart}`;
    const blob = new Blob([md], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `heartbeat-diagnostic-${ts}.md`;
    a.click();
    URL.revokeObjectURL(url);
  }, [data]);

  const handleDownloadJson = useCallback(() => {
    if (!data) return;
    const now = new Date();
    const datePart = now.toISOString().slice(0, 10);
    const timePart = now.toISOString().slice(11, 19).replace(/:/g, "");
    const ts = `${datePart}-${timePart}`;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `heartbeat-diagnostic-${ts}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [data]);

  const th = data?.tableHealth as (Record<string, Record<string, unknown>> & { timeMs?: number; error?: string }) | undefined;
  const pa = data?.pipelineAudit as Record<string, unknown> | undefined;
  const ed = data?.embeddingDiagnostic as Record<string, unknown> | undefined;
  const rd = data?.retrievalDiagnostic as Record<string, unknown> | undefined;
  const sd = data?.schedulerDiagnostic as Record<string, unknown> | undefined;
  const ds = data?.dataSourceConnections as (Record<string, Record<string, unknown>> & { timeMs?: number; error?: string }) | undefined;
  const gi = data?.graphIntegrity as Record<string, unknown> | undefined;
  const ci = data?.codeInspection as Record<string, unknown> | undefined;
  const ga = data?.gapAnalysis as Record<string, unknown> | undefined;
  const rts = data?.rawTableSchemas as Record<string, unknown> | undefined;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-500/20 border border-blue-500/30">
            <Stethoscope className="h-5 w-5 text-blue-700" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-[#1A1A1A]">Heartbeat Diagnostics</h2>
            <p className="text-sm text-[#746F66]">Complete system health report — read-only</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {data && (
            <>
              <Button size="sm" variant="outline" onClick={handleDownloadJson}
                className="border-[#E5E2DC] text-[#3D3A36] hover:text-[#1A1A1A] hover:bg-[#F0EDE7] gap-2">
                <Download className="h-4 w-4" />JSON
              </Button>
              <Button size="sm" onClick={handleDownloadMd}
                className="bg-[#1A1A1A] hover:bg-[#3D3A36] text-white gap-2">
                <Download className="h-4 w-4" />Download MD
              </Button>
            </>
          )}
          <Button size="sm" variant="outline" onClick={handleRefresh} disabled={isLoading}
            className="border-[#E5E2DC] text-[#3D3A36] hover:text-[#1A1A1A] hover:bg-[#F0EDE7] gap-2">
            <RefreshCw className={cn("h-4 w-4", isLoading && "animate-spin")} />
            {isLoading ? "Running…" : "Refresh"}
          </Button>
        </div>
      </div>

      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="rounded-xl border border-[#E5E2DC] bg-[#F0EDE7] p-4">
              <div className="flex items-center gap-3">
                <Skeleton className="h-5 w-5 rounded bg-[#F0EDE7]" />
                <Skeleton className="h-4 w-48 rounded bg-[#F0EDE7]" />
                <div className="flex-1" />
                <Skeleton className="h-5 w-20 rounded-full bg-[#F0EDE7]" />
              </div>
            </div>
          ))}
          <div className="flex items-center justify-center gap-2 pt-4 text-sm text-[#746F66]">
            <Loader2 className="h-4 w-4 animate-spin" />
            Running all diagnostic checks… this may take 5–15 seconds
          </div>
        </div>
      )}

      {error && !isLoading && (
        <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-5 text-red-700 text-sm">
          <p className="font-semibold mb-1">Diagnostic failed</p>
          <p>{(error as Error).message}</p>
        </div>
      )}

      {data && !isLoading && (
        <div className="space-y-3">
          <div className="rounded-xl border border-[#E5E2DC] bg-[#F0EDE7] p-4 flex flex-wrap gap-5">
            <div className="text-center">
              <div className="text-xl font-bold text-[#1A1A1A]">{((th?.chunks as Record<string, unknown>)?.totalActiveCount as number) ?? 0}</div>
              <div className="text-[11px] text-[#746F66]">Active Chunks</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-[#1A1A1A]">{((th?.connections as Record<string, unknown>)?.totalCount as number) ?? 0}</div>
              <div className="text-[11px] text-[#746F66]">Connections</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-[#1A1A1A]">
                {pa ? Object.entries(pa).filter(([k, v]) => k !== "timeMs" && (v as Record<string, unknown>).status === "operational").length : 0}/6
              </div>
              <div className="text-[11px] text-[#746F66]">Pipelines Active</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-red-700">{(ga?.issues as Array<{ severity: string }> ?? []).filter(i => i.severity === "critical").length}</div>
              <div className="text-[11px] text-[#746F66]">Critical Issues</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-orange-700">{(ga?.issues as Array<{ severity: string }> ?? []).filter(i => i.severity === "high").length}</div>
              <div className="text-[11px] text-[#746F66]">High Issues</div>
            </div>
            <div className="ml-auto text-center">
              <div className="text-xl font-bold text-[#3D3A36]">{formatMs((data.totalTimeMs as number) ?? 0)}</div>
              <div className="text-[11px] text-[#746F66]">Total Time</div>
            </div>
          </div>

          <SectionCard title="1. Database Table Health" icon={<FileText className="h-4 w-4" />}
            timeMs={th?.timeMs as number | undefined} statusEl={sectionHealthBadge(th as Record<string, unknown>)} defaultOpen>
            {th?.error ? (
              <div className="mt-4 rounded-lg bg-red-500/10 border border-red-500/20 p-3 text-sm text-red-700">{String(th.error)}</div>
            ) : (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-[#746F66] mb-2">Chunks</p>
                  <DataRow label="Active" value={(th?.chunks as Record<string, unknown>)?.totalActiveCount as number} />
                  <DataRow label="Archived" value={(th?.chunks as Record<string, unknown>)?.totalArchivedCount as number} />
                  <DataRow label="With Embeddings" value={(th?.chunks as Record<string, unknown>)?.chunksWithEmbeddings as number} />
                  <DataRow label="Without Embeddings" value={(th?.chunks as Record<string, unknown>)?.chunksWithoutEmbeddings as number} />
                  <DataRow label="Zero-Vector" value={(th?.chunks as Record<string, unknown>)?.chunksWithZeroEmbeddings as number} />
                  <DataRow label="Avg Importance" value={((th?.chunks as Record<string, unknown>)?.averageImportance as number)?.toFixed(2)} />
                  <DataRow label="Expired" value={(th?.chunks as Record<string, unknown>)?.expiredChunksCount as number} />
                  <DataRow label="Oldest" value={String((th?.chunks as Record<string, unknown>)?.oldestChunkDate ?? "—").slice(0, 10)} />
                  <DataRow label="Newest" value={String((th?.chunks as Record<string, unknown>)?.newestChunkDate ?? "—").slice(0, 10)} />
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-[#746F66] mb-2">By Category</p>
                  {Object.entries(((th?.chunks as Record<string, unknown>)?.countByCategory as Record<string, number>) ?? {}).map(([cat, cnt]) => (
                    <DataRow key={cat} label={cat} value={cnt} />
                  ))}
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-[#746F66] mt-3 mb-2">Connections</p>
                  <DataRow label="Total" value={(th?.connections as Record<string, unknown>)?.totalCount as number} />
                  <DataRow label="Strong (>=0.7)" value={(th?.connections as Record<string, unknown>)?.strongConnections as number} />
                  <DataRow label="Moderate" value={(th?.connections as Record<string, unknown>)?.moderateConnections as number} />
                  <DataRow label="Weak (<0.4)" value={(th?.connections as Record<string, unknown>)?.weakConnections as number} />
                  <DataRow label="Orphaned" value={(th?.connections as Record<string, unknown>)?.orphanedConnections as number} />
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-[#746F66] mt-3 mb-2">Digests</p>
                  <DataRow label="Total" value={(th?.digests as Record<string, unknown>)?.totalCount as number} />
                  <DataRow label="Most Recent" value={String((th?.digests as Record<string, unknown>)?.mostRecentDigestDate ?? "—").slice(0, 10)} />
                  {Object.entries(((th?.digests as Record<string, unknown>)?.countByType as Record<string, number>) ?? {}).map(([t, c]) => (
                    <DataRow key={t} label={t} value={c} />
                  ))}
                </div>
              </div>
            )}
          </SectionCard>

          <SectionCard title="2. Pipeline Status Audit" icon={<RefreshCw className="h-4 w-4" />}
            timeMs={pa?.timeMs as number | undefined} statusEl={pipelinesSectionBadge(pa)}>
            {pa?.error ? (
              <div className="mt-4 rounded-lg bg-red-500/10 border border-red-500/20 p-3 text-sm text-red-700">{String(pa.error)}</div>
            ) : (
              <div className="mt-4 space-y-3">
                {pa && Object.entries(pa).filter(([k]) => k !== "timeMs").map(([name, p]) => {
                  const pipeline = p as Record<string, unknown>;
                  return (
                    <div key={name} className="rounded-lg border border-[#E5E2DC] bg-[#F0EDE7] p-3">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-[12px] font-semibold text-[#1A1A1A] capitalize">{name.replace(/([A-Z])/g, " $1").trim()}</span>
                        <div className="flex-1" />
                        {statusBadge(String(pipeline.status))}
                      </div>
                      <DataRow label="Function" value={pipeline.functionName as string} />
                      {pipeline.chunksProduced !== undefined && <DataRow label="Chunks Produced" value={pipeline.chunksProduced as number} />}
                      {pipeline.digestsProduced !== undefined && <DataRow label="Digests Produced" value={pipeline.digestsProduced as number} />}
                      {pipeline.lastChunkCreated && <DataRow label="Last Chunk" value={String(pipeline.lastChunkCreated).slice(0, 10)} />}
                      {pipeline.lastDigestDate && <DataRow label="Last Digest" value={String(pipeline.lastDigestDate).slice(0, 10)} />}
                      {pipeline.scheduleExpression && <DataRow label="Schedule" value={pipeline.scheduleExpression as string} />}
                      {pipeline.triggerDescription && <DataRow label="Trigger" value={pipeline.triggerDescription as string} />}
                    </div>
                  );
                })}
              </div>
            )}
          </SectionCard>

          <SectionCard title="3. Embedding Service" icon={<FileCode className="h-4 w-4" />}
            timeMs={ed?.timeMs as number | undefined} statusEl={embeddingBadge(ed)}>
            {ed?.error ? (
              <div className="mt-4 rounded-lg bg-red-500/10 border border-red-500/20 p-3 text-sm text-red-700">{String(ed.error)}</div>
            ) : (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-[#746F66] mb-2">Service</p>
                  <DataRow label="Method" value={ed?.generationMethod as string} />
                  <DataRow label="Dimension" value={(ed?.testResults as Record<string, Record<string, unknown>>)?.test1?.outputDimension as number} />
                  <DataRow label="Has Valid Floats" value={String((ed?.testResults as Record<string, Record<string, unknown>>)?.test1?.hasValidFloats)} />
                  <DataRow label="All Zeros" value={String((ed?.testResults as Record<string, Record<string, unknown>>)?.test1?.isAllZeros)} />
                  <DataRow label="Differentiates Texts" value={String((ed?.testResults as Record<string, Record<string, unknown>>)?.test2?.isDifferentFromTest1)} />
                  <DataRow label="Deterministic" value={String((ed?.testResults as Record<string, Record<string, unknown>>)?.determinismTest?.isIdenticalToTest1)} />
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-[#746F66] mb-2">Chunks Analysis</p>
                  <DataRow label="With Embeddings" value={(ed?.chunksAnalysis as Record<string, unknown>)?.totalChunksWithEmbeddings as number} />
                  <DataRow label="Dimension Consistent" value={String((ed?.chunksAnalysis as Record<string, unknown>)?.embeddingDimensionConsistency)} />
                  <DataRow label="Corrupt Embeddings" value={(ed?.chunksAnalysis as Record<string, unknown>)?.anyCorruptEmbeddings as number} />
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-[#746F66] mt-3 mb-2">Sample Values (test 1)</p>
                  <div className="font-mono text-[11px] text-[#3D3A36] bg-[#F0EDE7] rounded p-2 break-all">
                    [{((ed?.testResults as Record<string, Record<string, unknown>>)?.test1?.sampleValues as number[] | undefined)?.map(v => v.toFixed(4)).join(", ")}]
                  </div>
                </div>
              </div>
            )}
          </SectionCard>

          <SectionCard title="4. Retrieval System" icon={<Eye className="h-4 w-4" />}
            timeMs={rd?.timeMs as number | undefined} statusEl={retrievalBadge(rd)}>
            {rd?.error ? (
              <div className="mt-4 rounded-lg bg-red-500/10 border border-red-500/20 p-3 text-sm text-red-700">{String(rd.error)}</div>
            ) : (
              <div className="mt-4">
                <DataRow label="AskMoji Integration" value={rd?.isIntegratedWithAskmoji ? "Yes" : "No"} />
                <DataRow label="Description" value={rd?.integrationDescription as string} />
                <DataRow label="Max Chunks" value={(rd?.retrievalConfig as Record<string, unknown>)?.maxResults as number} />
                <DataRow label="Max Tokens" value={(rd?.retrievalConfig as Record<string, unknown>)?.maxTokens as number} />
                <DataRow label="User Pins First" value={String((rd?.retrievalConfig as Record<string, unknown>)?.userPinsAlwaysIncluded)} />
                {(rd?.testQueries as Array<Record<string, unknown>> | undefined)?.length ? (
                  <div className="mt-3">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-[#746F66] mb-2">Test Queries</p>
                    {(rd.testQueries as Array<Record<string, unknown>>).map((q, i) => (
                      <div key={i} className="mb-2 rounded-lg bg-[#F0EDE7] p-3">
                        <p className="text-[12px] text-[#3D3A36] font-medium mb-1">"{String(q.query)}"</p>
                        {q.error ? (
                          <p className="text-[11px] text-red-700">{String(q.error)}</p>
                        ) : (
                          <p className="text-[11px] text-[#746F66]">{String(q.resultsReturned)} results · {String(q.timeMs)}ms{q.topResultPreview ? ` · "${String(q.topResultPreview).slice(0, 60)}…"` : ""}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            )}
          </SectionCard>

          <SectionCard title="5. Scheduler" icon={<Clock className="h-4 w-4" />}
            timeMs={sd?.timeMs as number | undefined} statusEl={schedulerBadge(sd)}>
            {sd?.error ? (
              <div className="mt-4 rounded-lg bg-red-500/10 border border-red-500/20 p-3 text-sm text-red-700">{String(sd.error)}</div>
            ) : (
              <div className="mt-4">
                <DataRow label="Service Exists" value={String(sd?.serviceExists)} />
                <DataRow label="Started on Boot" value={String(sd?.isStartedOnBoot)} />
                <DataRow label="Last Daily Run" value={sd?.lastDailyRun ? String(sd.lastDailyRun).slice(0, 16) : "Never"} />
                <DataRow label="Last Weekly Run" value={sd?.lastWeeklyRun ? String(sd.lastWeeklyRun).slice(0, 16) : "Never"} />
                <div className="mt-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-[#746F66] mb-2">Registered Jobs</p>
                  {(sd?.registeredJobs as Array<Record<string, string>> ?? []).map((job, i) => (
                    <div key={i} className="mb-1.5 rounded bg-[#F0EDE7] px-3 py-2">
                      <span className="text-[12px] font-medium text-[#1A1A1A]">{job.name}</span>
                      <span className="text-[11px] text-[#746F66] ml-2">{job.schedule}</span>
                      <p className="text-[11px] text-[#746F66]">{job.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </SectionCard>

          <SectionCard title="6. Data Source Connections" icon={<FileText className="h-4 w-4" />}
            timeMs={ds?.timeMs} statusEl={dataSourceBadge(ds as Record<string, unknown> | undefined)}>
            {ds?.error ? (
              <div className="mt-4 rounded-lg bg-red-500/10 border border-red-500/20 p-3 text-sm text-red-700">{String(ds.error)}</div>
            ) : (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-[#746F66] mb-2">Brands</p>
                  <DataRow label="Total" value={ds?.brands?.totalBrands as number} />
                  <DataRow label="With Analysis" value={ds?.brands?.brandsWithAnalysis as number} />
                  <DataRow label="Gap" value={ds?.brands?.gap as number} />
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-[#746F66] mt-3 mb-2">Ad Accounts</p>
                  <DataRow label="Total" value={ds?.adAccounts?.totalAccounts as number} />
                  <DataRow label="With Insights" value={ds?.adAccounts?.accountsWithInsights as number} />
                  <DataRow label="With Heartbeat Chunks" value={ds?.adAccounts?.accountsWithHeartbeatChunks as number} />
                  <DataRow label="With Performance Chunks" value={ds?.adAccounts?.accountsWithPerformanceChunks as number} />
                  <DataRow label="Gap" value={ds?.adAccounts?.gap as number} />
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-[#746F66] mb-2">Creatives</p>
                  <DataRow label="Platform Ads" value={ds?.creatives?.totalPlatformAds as number} />
                  <DataRow label="Heartbeat Tracked" value={ds?.creatives?.creativesWithHeartbeatTracking as number} />
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-[#746F66] mt-3 mb-2">Conversations</p>
                  <DataRow label="Total Sessions" value={ds?.conversations?.totalConversations as number} />
                  <DataRow label="With 8+ Messages" value={ds?.conversations?.conversationsWithEightPlusMessages as number} />
                  <DataRow label="Extracted Decisions" value={ds?.conversations?.conversationsWithExtractedDecisions as number} />
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-[#746F66] mt-3 mb-2">Competitors</p>
                  <DataRow label="Boards" value={ds?.competitors?.totalCompetitorBoards as number} />
                  <DataRow label="Completed" value={ds?.competitors?.completedBoards as number} />
                  <DataRow label="Meta Insight Rows" value={ds?.metaAds?.metaInsightsRowCount as number} />
                </div>
              </div>
            )}
          </SectionCard>

          <SectionCard title="7. Graph Integrity" icon={<FileText className="h-4 w-4" />}
            timeMs={gi?.timeMs as number | undefined} statusEl={graphBadge(gi)}>
            {gi?.error ? (
              <div className="mt-4 rounded-lg bg-red-500/10 border border-red-500/20 p-3 text-sm text-red-700">{String(gi.error)}</div>
            ) : (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <DataRow label="Total Nodes" value={gi?.totalNodes as number} />
                  <DataRow label="Total Edges" value={gi?.totalEdges as number} />
                  <DataRow label="Orphaned Edges" value={gi?.orphanedEdges as number} />
                  <DataRow label="Duplicate Edges" value={gi?.duplicateEdges as number} />
                  <DataRow label="Self Edges" value={gi?.selfEdges as number} />
                  <DataRow label="Isolated Nodes" value={gi?.isolatedNodes as number} />
                  <DataRow label="Avg Connections/Node" value={(gi?.averageConnectionsPerNode as number)?.toFixed(2)} />
                  {(gi?.maxConnectionsOnSingleNode as Record<string, unknown> | null) && (
                    <DataRow label="Most Connected" value={`Chunk #${(gi.maxConnectionsOnSingleNode as Record<string, unknown>).chunkId} (${(gi.maxConnectionsOnSingleNode as Record<string, unknown>).connectionCount} connections)`} />
                  )}
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-[#746F66] mb-2">Strength Distribution</p>
                  {Object.entries((gi?.strengthDistribution as Record<string, number>) ?? {}).map(([bucket, count]) => (
                    <DataRow key={bucket} label={bucket} value={count} />
                  ))}
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-[#746F66] mt-3 mb-2">Category Matrix</p>
                  {Object.entries((gi?.categoryConnectionMatrix as Record<string, number>) ?? {}).slice(0, 8).map(([pair, count]) => (
                    <DataRow key={pair} label={pair} value={count} />
                  ))}
                </div>
              </div>
            )}
          </SectionCard>

          <SectionCard title="8. Code Inspection" icon={<FileCode className="h-4 w-4" />}
            timeMs={ci?.timeMs as number | undefined} statusEl={codeBadge(ci)}>
            {ci?.error ? (
              <div className="mt-4 rounded-lg bg-red-500/10 border border-red-500/20 p-3 text-sm text-red-700">{String(ci.error)}</div>
            ) : (
              <div className="mt-4 space-y-2">
                {Object.entries((ci?.files as Record<string, Record<string, unknown>>) ?? {}).map(([filePath, info]) => (
                  <div key={filePath} className="flex items-start gap-3 py-1.5 border-b border-[#E5E2DC]">
                    {info.exists ? <CheckCircle className="h-4 w-4 text-emerald-700 flex-shrink-0 mt-0.5" /> : <XCircle className="h-4 w-4 text-red-700 flex-shrink-0 mt-0.5" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] font-mono text-[#3D3A36]">{filePath}</p>
                      {info.exists && (
                        <p className="text-[11px] text-[#746F66]">{String(info.estimatedLineCount)} lines · exports: {(info.exportedFunctions as string[])?.slice(0, 6).join(", ") || "none"}</p>
                      )}
                      {!info.exists && <p className="text-[11px] text-red-700">File not found</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>

          <SectionCard title="9. Gap Analysis & Issues" icon={<AlertTriangle className="h-4 w-4" />}
            timeMs={ga?.timeMs as number | undefined} statusEl={gapBadge(ga)} defaultOpen>
            {ga?.error ? (
              <div className="mt-4 rounded-lg bg-red-500/10 border border-red-500/20 p-3 text-sm text-red-700">{String(ga.error)}</div>
            ) : (
              <div className="mt-4">
                {((ga?.issues as Array<Record<string, unknown>>) ?? []).length === 0 ? (
                  <div className="flex items-center gap-2 text-emerald-700 text-sm">
                    <CheckCircle className="h-4 w-4" />
                    No issues detected. System appears healthy.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {(ga!.issues as Array<Record<string, unknown>>).map((issue, i) => (
                      <div key={i} className="rounded-lg border border-[#E5E2DC] bg-[#F0EDE7] p-3">
                        <div className="flex items-start gap-2 mb-2">
                          {severityIcon(String(issue.severity))}
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-[12px] font-semibold text-[#1A1A1A]">{String(issue.title)}</span>
                              {severityBadge(String(issue.severity))}
                            </div>
                            <span className="text-[11px] text-[#746F66]">{String(issue.category)}</span>
                          </div>
                        </div>
                        <p className="text-[12px] text-[#3D3A36] mb-1">{String(issue.description)}</p>
                        <p className="text-[12px] text-blue-700">→ {String(issue.recommendation)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </SectionCard>

          <SectionCard title="10. Raw Table Schemas" icon={<FileCode className="h-4 w-4" />}
            timeMs={rts?.timeMs as number | undefined} statusEl={schemaBadge(rts)}>
            {rts?.error ? (
              <div className="mt-4 rounded-lg bg-red-500/10 border border-red-500/20 p-3 text-sm text-red-700">{String(rts.error)}</div>
            ) : (
              <div className="mt-4 space-y-4">
                {Object.entries((rts?.schemas as Record<string, Array<Record<string, unknown>>>) ?? {}).map(([table, cols]) => (
                  <div key={table}>
                    <p className="text-[12px] font-semibold font-mono text-[#3D3A36] mb-2">{table}</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-[11px]">
                        <thead>
                          <tr className="border-b border-[#E5E2DC]">
                            <th className="text-left py-1 px-2 text-[#746F66] font-medium">Column</th>
                            <th className="text-left py-1 px-2 text-[#746F66] font-medium">Type</th>
                            <th className="text-left py-1 px-2 text-[#746F66] font-medium">Nullable</th>
                            <th className="text-left py-1 px-2 text-[#746F66] font-medium">Default</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cols.map((col, i) => (
                            <tr key={i} className="border-b border-[#E5E2DC]">
                              <td className="py-1 px-2 font-mono text-[#3D3A36]">{String(col.column_name)}</td>
                              <td className="py-1 px-2 text-[#746F66]">{String(col.data_type)}</td>
                              <td className="py-1 px-2 text-[#746F66]">{String(col.is_nullable)}</td>
                              <td className="py-1 px-2 text-[#746F66]">{col.column_default != null ? String(col.column_default) : "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </div>
      )}
    </div>
  );
}

// ── Main Page ──

type Tab = "report" | "diagnostic";

export default function PlatformDocs() {
  useAdminGuard();
  const [activeTab, setActiveTab] = useState<Tab>("report");

  return (
    <div className="min-h-screen bg-[#F5F3EE] text-[#1A1A1A]">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <FileSearch className="h-5 w-5 text-indigo-700" />
          <h1 className="text-xl font-bold text-[#1A1A1A]">Platform Docs</h1>
        </div>
        <p className="text-sm text-[#746F66]">Dynamic platform report and Heartbeat diagnostics — export for external AI analysis</p>
      </div>

      <div className="flex gap-1 mb-6 border-b border-[#E5E2DC]">
        {([
          { key: "report", icon: FileSearch, label: "Platform Report" },
          { key: "diagnostic", icon: Stethoscope, label: "Heartbeat Diagnostics" },
        ] as { key: Tab; icon: React.ElementType; label: string }[]).map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            data-testid={`tab-${tab.key}`}
            className={cn(
              "flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px",
              activeTab === tab.key
                ? "border-white text-[#1A1A1A]"
                : "border-transparent text-[#746F66] hover:text-[#1A1A1A]"
            )}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "report" && <PlatformReportTab />}
      {activeTab === "diagnostic" && <HeartbeatDiagnosticTab />}
    </div>
  );
}
